define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem
) {
    'use strict'

    class ImageLayer {
        /**
         * @description 地形影像数据路径。
         * @type {String}
         */
        url
        /**
         * @description 地形影像数据显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 是否为大文件格式数据。
         * @type {Boolean}
         * @readonly
         */
        bDataBase
        /**
         * @description 地形影像数据坐标参考系。
         * @type {String}
         * @readonly
         */
        spatialReference
        /**
         * @description 地形影像数据绘制顺序，数字越小越先绘。
         * @type {Number}
         * @readonly
         */
        displayOrder
        /**
         * @description 地形影像数据图片格式。
         * @type {String}
         * @readonly
         */
        format
        /**
         * @description 地形影像数据瓦片大小。
         * @type {String}
         * @readonly
         */
        tileSize
        /**
         * @description 地形影像数据范围最小经度。
         * @type {Number}
         * @readonly
         */
        minX
        /**
         * @description 地形影像数据范围最大经度。
         * @type {Number}
         * @readonly
         */
        maxX
        /**
         * @description 地形影像数据范围最小纬度。
         * @type {Number}
         * @readonly
         */
        minY
        /**
         * @description 地形影像数据范围最大纬度。
         * @type {Number}
         * @readonly
         */
        maxY
        /**
         * @description 地形影像数据最小显示层级。
         * @type {Number}
         * @readonly
         */
        minLevel
        /**
         * @description 地形影像数据最大显示层级。
         * @type {Number}
         * @readonly
         */
        maxLevel
        /**
         * @description 地形影像对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 地形影像组件uuid。
         * @type {String}
         * @readonly
         */
        componentUuid = null
        /**
         * @description MeshLayer在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "ImageLayer"
        static avatarType = "deCore.Layers.ImageLayer"
        static componentDataSourceType = "AvatarComponent.DataSource.MTDSSourceComponent"
        /**
         * 地形影像图层。
         * <br /><br />
         * <div align='center'>
         * <img src='images/ImageLayer.png' width='900' height='650' /><br />
         * 地形影像效果图
         * </div>
         * 
         * @demo {@link http://lbs.zjkelan.com/ImageLayer.html|CooWasm ImageLayer demo}
         * @alias ImageLayer
         * @constructor
         *
         * @param {Object} props 包含如下属性的Object：
         * @param {WMSInformation} [props.wmsInfo] WMS图层信息，与WMTS互斥。
         * @param {WMTSInformation} [props.wmtsInfo] WMTS图层信息，与WMS互斥。
         * @param {String} props.url 地形影像数据的路径，可以是WMS、WMTS的服务地址，也可以是xml文件路径。
         * @param {Number} [props.minLevel=0] 地形影像数据最小显示层级。
         * @param {Number} [props.maxLevel=16] 地形影像数据最大显示层级。
         * @param {Number} [props.displayOrder] 地形影像数据绘制顺序，数字越小越先绘。
         * @param {Boolean} [props.bVisible=true] 地形影像数据加载时显隐状态。
         * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
         * @example
         * var layer = new CooWasm.ImageLayer({
         *     url:"http://127.0.0.1/DOM/metadata.xml"
         * },(state)=>{console.log("load status:" + state)})
         */
        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                return
            }
            if (!defined(props.url)) {
                console.log("ImageLayer need url!")
                return
            }
            let bWMS = false
            let bWMTS = false
            if(defined(props.wmsInfo)){
                bWMS = true
                bWMTS = false
            } else if(defined(props.wmtsInfo)){
                bWMTS = true
                bWMS = false
            }
            this.url = props.url
            this.bVisible = defaultValue(props.bVisible, true)
            this.minLevel = defaultValue(props.minLevel, 0)
            this.maxLevel = defaultValue(props.maxLevel, 16)
            var connectUrl = encode(this.url)
            var aCategory = encode("deCore.Layers")
            var aName = encode("ImageLayer")
            var cCategory = encode("AvatarComponent.DataSource")
            var cName = encode("MTDSSourceComponent")
            
            var aProps = ""
            var cProps = ""

            aProps += this.toStr("bVisible", props.bVisible, "Visible")
            
            if(bWMS){
                cName = encode("WMSSourceComponent")
                cProps += this.toStr("url", this.url, "URL")
                cProps += this.toStr("layerName", props.wmsInfo.layerName, "LayerName")
                cProps += this.toStr("version", props.wmsInfo.version, "Version")
                cProps += this.toStr("style", props.wmsInfo.style, "Style")
                cProps += this.toStr("srs", props.wmsInfo.srs, "SpatialReference")
                cProps += this.toStr("format", props.wmsInfo.format, "Format")
                cProps += this.toStr("minX", props.wmsInfo.minX, "MinX")
                cProps += this.toStr("minY", props.wmsInfo.minY, "MinY")
                cProps += this.toStr("maxX", props.wmsInfo.maxX, "MaxX")
                cProps += this.toStr("maxY", props.wmsInfo.maxY, "MaxY")
                cProps += this.toStr("minLevel", this.minLevel, "MinLevel")
                cProps += this.toStr("maxLevel", this.maxLevel, "MaxLevel")
                cProps += this.toStr("displayOrder", props.displayOrder, "DisplayOrder")
            } else if(bWMTS){
                cName = encode("WMTSSourceComponent")
                cProps += this.toStr("url", this.url, "URL")
                cProps += this.toStr("layerName", props.wmtsInfo.layerName, "LayerName")
                cProps += this.toStr("style", props.wmtsInfo.style, "Style")
                cProps += this.toStr("srs", props.wmtsInfo.srs, "SpatialReference")
                cProps += this.toStr("format", props.wmtsInfo.format, "Format")
                cProps += this.toStr("minX", props.wmtsInfo.minX, "MinX")
                cProps += this.toStr("minY", props.wmtsInfo.minY, "MinY")
                cProps += this.toStr("maxX", props.wmtsInfo.maxX, "MaxX")
                cProps += this.toStr("maxY", props.wmtsInfo.maxY, "MaxY")
                this.minLevel = defaultValue(props.minLevel, props.wmtsInfo.minLevel)
                this.maxLevel = defaultValue(props.maxLevel, props.wmtsInfo.maxLevel)
                cProps += this.toStr("minLevel", this.minLevel, "MinLevel")
                cProps += this.toStr("maxLevel", this.maxLevel, "MaxLevel")
                cProps += this.toStr("tileMatrixSet", props.wmtsInfo.tileMatrixSet, "TileMatrixSet")
                cProps += this.toStr("dpi", props.wmtsInfo.dpi, "DPI")
                cProps += this.toStr("customParams", props.wmtsInfo.customParams, "CustomParams")
                cProps += this.toStr("displayOrder", props.displayOrder, "DisplayOrder")
            }
            var idInfo = Element.prototype.NativeAPI.LoadAvatarAndComponent(connectUrl, aCategory, aName, encode(aProps), cCategory, cName, encode(cProps), encode(""), encode(""), this.bVisible, true)
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                this.componentUuid = idArray[1]
                //
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                    Element.prototype.Map.imageLayers.push(this)
                })
                //console.log("ImageLayer avatarUuid:" + this.avatarUuid + " componentUuid:" + this.componentUuid)
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }

        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         * <p>
         * 一般用于多个属性的同时更新，避免性能开销过大。（待启用）
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {Boolean} [props.bVisible=true] 地形影像数据加载时显隐状态。
         * @param {CallbackBoolean} [callback=null] 更新成功后触发的函数回调。
         * @example
         * layer.update({
         *      bVisible:false
         * },(state)=>{console.log("update status:" + state)})
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            if (defined(props)) {
                if (defined(props.bVisible)) {
                    this.bVisible = props.bVisible
                    avatarProps += "Visible:" + this.bVisible + "|"
                }
                callbackSystem.register("UpdateAvatarAndComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndComponent(encode(this.avatarUuid), encode(this.componentUuid), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }


        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * // 定位图层
         * layer.locate(()=>{console.log("locate done!")})
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取图层包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * // 获取属性
         * layer.getProperties()
         * console.log(layer.spatialReference)
         */
        getProperties() {
            if (!defined(this.avatarUuid) || !defined(this.componentUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                this.componentUuid = uuid
                for (let z = 0; z < propSize; ++z) {
                    let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                    let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                    let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                    if (name == "Compress") {
                        if (value == "true" || value == "1") {
                            this.bCompress = true
                        } else {
                            this.bCompress = false
                        }
                    } else if (name == "DataBase") {
                        if (value == "true" || value == "1") {
                            this.bDataBase = true
                        } else {
                            this.bDataBase = false
                        }
                    } else if (name == "SpatialReference") {
                        this.spatialReference = value
                    } else if (name == "DisplayOrder") {
                        this.displayOrder = Number(value)
                    } else if (name == "Format") {
                        this.format = value
                    } else if (name == "TileSize") {
                        this.tileSize = Number(value)
                    } else if (name == "MinX") {
                        this.minX = Number(value)
                    } else if (name == "MaxX") {
                        this.maxX = Number(value)
                    } else if (name == "MinY") {
                        this.minY = Number(value)
                    } else if (name == "MaxY") {
                        this.maxY = Number(value)
                    } else if (name == "MinLevel") {
                        this.minLevel = Number(value)
                    } else if (name == "MaxLevel") {
                        this.maxLevel = Number(value)
                    } else if( name == "TileMatrixSet") {
                        this.tileMatrixSet = value
                    } else if( name == "DPI") {
                        this.dpi = Number(value)
                    } else if( name == "CustomParams") {
                        this.customParams = value
                    } else if( name == "style") {
                        this.style = value
                    } else if( name == "layerName") {
                        this.layerName = value
                    }

                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁图层后触发的函数回调。
         * @returns null 空值。
         * @example
         * // 销毁图层
         * layer = layer.destroy((state)=>{console.log("destroy status:" + state)})
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.imageLayers = Element.prototype.Map.imageLayers.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return ImageLayer
})